import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  ClipboardCheck, Check, X, Clock, BookOpen, AlertCircle, CheckCircle2, Pencil, Save
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKING_LABELS = {
  bq_abo: "BQ-Abonnement", hq_abo: "HQ-Abonnement",
  bq_intensiv: "BQ-Intensivkurs", hq_intensiv: "HQ-Intensivkurs",
  probetraining: "Probetraining", mathe_grundkurs: "Mathe-Grundkurs",
  fachgespraech: "Fachgespräch",
};

const BQ_SUBJECTS = [
  { id: "NTG", label: "NTG" }, { id: "BWH", label: "BWH" },
  { id: "ZIB", label: "ZIB" }, { id: "RBH", label: "RBH" }, { id: "MIKP", label: "MIKP" },
];

const HQ_SUBJECTS = [
  { id: "orga", label: "Organisation" },
  { id: "betriebliches_kostenwesen", label: "Betriebl. Kostenwesen" },
  { id: "montagetechnik", label: "Montagetechnik" },
  { id: "fertigungstechnik", label: "Fertigungstechnik" },
  { id: "betriebstechnik", label: "Betriebstechnik" },
];

const StatusBadge = ({ status }) => {
  const map = {
    pending_approval: { label: "Ausstehend", cls: "bg-amber-500/10 text-amber-500 border-amber-500/30", icon: Clock },
    approved: { label: "Freigegeben", cls: "bg-green-500/10 text-green-500 border-green-500/30", icon: CheckCircle2 },
    rejected: { label: "Abgelehnt", cls: "bg-red-500/10 text-red-500 border-red-500/30", icon: X },
    active: { label: "Aktiv", cls: "bg-green-500/10 text-green-500 border-green-500/30", icon: CheckCircle2 },
    expired: { label: "Abgelaufen", cls: "bg-gray-500/10 text-gray-500 border-gray-500/30", icon: AlertCircle },
    expiring_soon: { label: "Bald ablaufend", cls: "bg-amber-500/10 text-amber-500 border-amber-500/30", icon: Clock },
  };
  const m = map[status] || map.active;
  const Icon = m.icon;
  return (
    <Badge className={`${m.cls} border`}>
      <Icon className="h-3 w-3 mr-1" />{m.label}
    </Badge>
  );
};

const BookingApproval = () => {
  const [pending, setPending] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [editBooking, setEditBooking] = useState(null);
  const [editSubjects, setEditSubjects] = useState([]);

  const loadPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings/pending`);
      setPending(res.data);
    } catch (e) { /* silent */ }
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`);
      const all = [];
      for (const u of res.data) {
        for (const b of (u.bookings || [])) {
          all.push({ ...b, user: { name: u.name, email: u.email } });
        }
      }
      all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setAllBookings(all);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    Promise.all([loadPending(), loadAll()]).finally(() => setLoading(false));
  }, [loadPending, loadAll]);

  const refresh = () => Promise.all([loadPending(), loadAll()]);

  const handleApprove = async (bookingId) => {
    setProcessing(bookingId);
    try {
      await axios.put(`${API}/bookings/${bookingId}/approve`);
      toast.success("Buchung freigegeben");
      await refresh();
    } catch (e) { toast.error("Fehler"); }
    finally { setProcessing(null); }
  };

  const handleReject = async (bookingId) => {
    setProcessing(bookingId);
    try {
      await axios.put(`${API}/bookings/${bookingId}/reject`);
      toast.success("Buchung abgelehnt");
      await refresh();
    } catch (e) { toast.error("Fehler"); }
    finally { setProcessing(null); }
  };

  const openEditDialog = (booking) => {
    setEditBooking(booking);
    setEditSubjects([...(booking.subjects || [])]);
  };

  const toggleSubject = (subjectId) => {
    setEditSubjects(prev =>
      prev.includes(subjectId) ? prev.filter(s => s !== subjectId) : [...prev, subjectId]
    );
  };

  const handleSaveSubjects = async () => {
    if (!editBooking) return;
    setProcessing(editBooking.id);
    try {
      await axios.put(`${API}/bookings/${editBooking.id}`, { subjects: editSubjects });
      toast.success("Fächer aktualisiert");
      setEditBooking(null);
      await refresh();
    } catch (e) { toast.error("Fehler beim Speichern"); }
    finally { setProcessing(null); }
  };

  const getSubjectsForType = (bookingType) => {
    if (bookingType === "bq_intensiv") return BQ_SUBJECTS;
    if (bookingType === "hq_intensiv") return HQ_SUBJECTS;
    return [];
  };

  const canEditSubjects = (b) => b.booking_type === "bq_intensiv" || b.booking_type === "hq_intensiv";

  const BookingRow = ({ b, showActions }) => (
    <TableRow data-testid={`booking-row-${b.id}`}>
      <TableCell className="font-medium">{b.user?.name || "-"}</TableCell>
      <TableCell className="text-muted-foreground text-sm">{b.user?.email || "-"}</TableCell>
      <TableCell>{BOOKING_LABELS[b.booking_type] || b.booking_type}</TableCell>
      <TableCell>
        {b.subjects?.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {b.subjects.map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
          </div>
        ) : b.hq_sub_type ? (
          <Badge variant="outline" className="text-xs">{b.hq_sub_type}</Badge>
        ) : <span className="text-muted-foreground text-xs">-</span>}
      </TableCell>
      <TableCell>{b.start_date || "-"}</TableCell>
      <TableCell><StatusBadge status={b.status || b.approval_status} /></TableCell>
      <TableCell className="text-right">
        <div className="flex gap-1 justify-end">
          {canEditSubjects(b) && (
            <Button size="sm" variant="outline" onClick={() => openEditDialog(b)} data-testid={`edit-subjects-${b.id}`}>
              <Pencil className="h-3 w-3 mr-1" />Fächer
            </Button>
          )}
          {showActions && (b.approval_status === "pending" || b.status === "pending_approval") && (
            <>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove(b.id)} disabled={processing === b.id} data-testid={`approve-${b.id}`}>
                <Check className="h-3 w-3 mr-1" />Freigeben
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleReject(b.id)} disabled={processing === b.id} data-testid={`reject-${b.id}`}>
                <X className="h-3 w-3 mr-1" />Ablehnen
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <AdminLayout title="Buchungsfreigabe">
      <div className="space-y-6" data-testid="booking-approval">
        {pending.length > 0 && (
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="py-4 flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-500" />
              <span className="font-medium text-amber-500">
                {pending.length} Buchung{pending.length !== 1 ? "en" : ""} warten auf Freigabe
              </span>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="pending">
          <TabsList className="grid w-full grid-cols-2 bg-secondary/50">
            <TabsTrigger value="pending" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Clock className="h-4 w-4" />Ausstehend ({pending.length})
            </TabsTrigger>
            <TabsTrigger value="all" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <BookOpen className="h-4 w-4" />Alle Buchungen
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-primary" />Ausstehende Freigaben
                </CardTitle>
                <CardDescription>Buchungsanfragen prüfen, Fächer anpassen und freigeben oder ablehnen.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
                ) : pending.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground"><CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>Keine ausstehenden Anfragen</p></div>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Name</TableHead><TableHead>E-Mail</TableHead><TableHead>Buchungstyp</TableHead>
                      <TableHead>Fächer</TableHead><TableHead>Start</TableHead><TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>{pending.map(b => <BookingRow key={b.id} b={b} showActions />)}</TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="all">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" />Alle Buchungen</CardTitle>
                <CardDescription>Vollständige Übersicht aller Buchungen. Fächer können jederzeit bearbeitet werden.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Name</TableHead><TableHead>E-Mail</TableHead><TableHead>Buchungstyp</TableHead>
                      <TableHead>Fächer</TableHead><TableHead>Start</TableHead><TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>{allBookings.map(b => <BookingRow key={b.id} b={b} showActions={b.approval_status === "pending"} />)}</TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Subjects Dialog */}
        <Dialog open={!!editBooking} onOpenChange={(open) => !open && setEditBooking(null)}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5 text-primary" />
                Fächer bearbeiten
              </DialogTitle>
              <DialogDescription>
                {editBooking?.user?.name} - {BOOKING_LABELS[editBooking?.booking_type]}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label className="font-medium">
                {editBooking?.booking_type === "bq_intensiv" ? "BQ-Fächer" : "HQ-Fächer"} auswählen:
              </Label>
              <div className="grid grid-cols-1 gap-2">
                {editBooking && getSubjectsForType(editBooking.booking_type).map(subj => {
                  const checked = editSubjects.includes(subj.id);
                  return (
                    <div
                      key={subj.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        checked ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/30"
                      }`}
                      onClick={() => toggleSubject(subj.id)}
                      data-testid={`edit-subject-${subj.id}`}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleSubject(subj.id)} className="w-4 h-4 accent-primary" />
                      <span className="text-sm font-medium">{subj.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditBooking(null)}>Abbrechen</Button>
              <Button onClick={handleSaveSubjects} disabled={processing === editBooking?.id} data-testid="save-subjects-btn">
                <Save className="h-4 w-4 mr-2" />Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default BookingApproval;
